/**
 * Samples the local user's pose (head, hands, finger curl, locomotion and voice state) into a
 * PoseData frame for the 20 Hz broadcast.
 */
import * as THREE from 'three';
import { STANDING_EYE_HEIGHT } from './PlayerRig.ts';
import type { InputManager } from './InputManager.ts';
import type { PlayerRig } from './PlayerRig.ts';
import type { PoseData, PoseHand } from '@clinspace/shared';

const _p = new THREE.Vector3();
const _q = new THREE.Quaternion();

export class LocalPose {
  /** Set by locomotion for one frame after a teleport. */
  teleported = false;
  smoothMoving = false;
  speaking = false;

  constructor(
    private readonly rig: PlayerRig,
    private readonly input: InputManager,
  ) {}

  /** The tallest calibrated eye height seen this session. */
  private peak = 0;

  /**
   * Standing eye height, which sizes the avatar others see: the tallest calibrated head
   * height this session, never under 1.55 m. It used to be the head height of the moment,
   * so a seated user showed as a small child (2026-10-05, headset).
   */
  standingHeight(inXr: boolean): number {
    if (!inXr) return STANDING_EYE_HEIGHT;
    const tracked = this.rig.camera.position.y + this.rig.heightOffset;
    if (tracked < 2.1) this.peak = Math.max(this.peak, tracked);
    return THREE.MathUtils.clamp(this.peak, 1.55, 2.1);
  }

  sample(inXr: boolean, now = performance.now()): PoseData {
    this.rig.camera.getWorldPosition(_p);
    this.rig.camera.getWorldQuaternion(_q);
    const hands: [PoseHand | null, PoseHand | null] = [null, null];
    if (inXr) {
      for (let i = 0; i < this.input.slotCount; i++) {
        const h = this.input.handPose(i);
        if (!h) continue;
        const slot = h.handedness === 'left' ? 0 : h.handedness === 'right' ? 1 : hands[0] ? 1 : 0;
        if (hands[slot]) continue;
        hands[slot] = {
          p: [round(h.position.x), round(h.position.y), round(h.position.z)],
          q: [
            round(h.quaternion.x, 4),
            round(h.quaternion.y, 4),
            round(h.quaternion.z, 4),
            round(h.quaternion.w, 4),
          ],
          kind: h.kind,
          pinch: round(h.pinch, 2),
          curl: h.curl.map((c) => round(c, 2)) as PoseHand['curl'],
        };
      }
    }
    const moving: PoseData['moving'] = this.teleported ? 2 : this.smoothMoving ? 1 : 0;
    this.teleported = false;
    return {
      t: Math.round(now),
      head: [
        round(_p.x),
        round(_p.y),
        round(_p.z),
        round(_q.x, 4),
        round(_q.y, 4),
        round(_q.z, 4),
        round(_q.w, 4),
      ],
      hands,
      moving,
      speaking: this.speaking,
      height: round(this.standingHeight(inXr), 2),
    };
  }
}

function round(v: number, decimals = 3): number {
  const f = 10 ** decimals;
  return Math.round(v * f) / f;
}
